import React, { useRef, useState } from 'react';

const UploadImage = ({ onUpload, onError }) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  
  // Handle file selection
  const handleFile = (file) => {
    if (!file) return;
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      onError('Invalid file type. Please upload a JPEG or PNG image.');
      return;
    }
    
    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      onError('File is too large. Maximum size is 5MB.');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
      onUpload(e.target.result);
    };
    reader.onerror = () => {
      onError('Error reading file. Please try again.');
    };
    reader.readAsDataURL(file);
  };
  
  // Handle file input change
  const handleChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  
  // Handle button click
  const handleButtonClick = () => {
    fileInputRef.current.click();
  };
  
  // Clear the selected image
  const clearImage = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="card p-4">
      <h2 className="text-xl font-semibold mb-4 text-center">Upload Image</h2>
      
      {!previewUrl ? (
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center ${
            dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          
          <p className="mt-2 text-gray-600">
            Drag and drop your image here, or
          </p>
          
          <button
            type="button"
            onClick={handleButtonClick}
            className="mt-2 btn-primary"
          >
            Browse Files
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleChange}
          />
          
          <p className="mt-2 text-xs text-gray-500">
            Supported formats: JPEG, PNG. Max size: 5MB
          </p>
        </div>
      ) : (
        <div className="relative max-w-md mx-auto">
          <img
            src={previewUrl}
            alt="Uploaded"
            className="w-full h-auto rounded-lg shadow-sm max-h-96 object-contain"
          />
          
          <button
            onClick={clearImage}
            className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
            aria-label="Clear image"
          >
            <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      <p className="mt-4 text-sm text-center text-gray-500">
        For best results, use an image with your face clearly visible and looking straight at the camera.
      </p>
    </div>
  );
};

export default UploadImage;